import { CartLinesUpdateEvent } from '@shopify/events';

/**
 * Self-contained "Quick View" modal for collection product cards.
 *
 * Deliberately independent of Heritage's native quick-add / quick-add-modal system: fetches its
 * data via the alternate template `templates/product.quickview.json` + `sections/vc-quick-view-data.liquid`
 * (Section Rendering API, `/products/{handle}?view=quickview&section_id=vc-quick-view-data`) and
 * builds its own markup from the response. That alternate template is a file completely separate
 * from `templates/product.json` -- the real product page is untouched, both in code and in the
 * request path (the alternate template is inert unless a client explicitly asks for
 * `?view=quickview`).
 *
 * Opened by clicking any `[data-vc-quick-view-trigger]` element anywhere on the page; the
 * trigger's `data-product-handle` attribute identifies which product to load.
 */
class VcQuickView extends HTMLElement {
  /** @type {HTMLDialogElement | null} */
  #dialog = null;
  /** @type {Array<{src: string, alt: string}>} */
  #images = [];
  /** @type {number} */
  #activeImageIndex = 0;
  /** @type {object | null} */
  #product = null;
  /** @type {object | null} */
  #selectedVariant = null;
  /** @type {Record<string, string>} keyed by 1-based option position, matching variant.options[] */
  #selectedOptions = {};

  connectedCallback() {
    this.#dialog = this.querySelector('dialog');
    if (!this.#dialog) return;

    this.querySelector('[data-vc-qv-close]')?.addEventListener('click', () => this.close());
    this.#dialog.addEventListener('click', (event) => {
      if (event.target === this.#dialog) this.close();
    });
    this.#dialog.addEventListener('close', () => this.#reset());

    this.querySelector('[data-vc-qv-prev]')?.addEventListener('click', () => this.#showImage(this.#activeImageIndex - 1));
    this.querySelector('[data-vc-qv-next]')?.addEventListener('click', () => this.#showImage(this.#activeImageIndex + 1));
    this.querySelector('[data-vc-qv-thumb-prev]')?.addEventListener('click', () => this.#scrollThumbs(-1));
    this.querySelector('[data-vc-qv-thumb-next]')?.addEventListener('click', () => this.#scrollThumbs(1));

    this.querySelector('[data-vc-qv-variants]')?.addEventListener('click', (event) => {
      const button = /** @type {Element} */ (event.target).closest('.vc-quick-view__swatch-btn');
      if (button) this.#selectSwatch(/** @type {HTMLButtonElement} */ (button));
    });

    this.querySelector('[data-vc-qv-qty-minus]')?.addEventListener('click', () => this.#adjustQuantity(-1));
    this.querySelector('[data-vc-qv-qty-plus]')?.addEventListener('click', () => this.#adjustQuantity(1));

    this.querySelector('[data-vc-qv-add-to-cart]')?.addEventListener('click', () => this.#addToCart());

    this.#watchForInstallmentsPopup();

    document.addEventListener('click', this.#handleTriggerClick);
  }

  disconnectedCallback() {
    document.removeEventListener('click', this.#handleTriggerClick);
  }

  #handleTriggerClick = (/** @type {MouseEvent} */ event) => {
    if (!(event.target instanceof Element)) return;
    const trigger = event.target.closest('[data-vc-quick-view-trigger]');
    if (!trigger) return;
    event.preventDefault();
    event.stopPropagation();
    const handle = /** @type {HTMLElement} */ (trigger).dataset.productHandle;
    if (handle) this.open(handle);
  };

  /** @param {string} handle */
  async open(handle) {
    if (!this.#dialog) return;
    this.#setLoading(true);
    this.#dialog.showModal();

    try {
      const response = await fetch(`/products/${handle}?view=quickview&section_id=vc-quick-view-data`);
      if (!response.ok) throw new Error(`Failed to load product: HTTP ${response.status}`);
      const html = await response.text();
      const doc = new DOMParser().parseFromString(html, 'text/html');

      const payloadScript = doc.querySelector('[data-vc-qv-payload]');
      if (!payloadScript?.textContent) throw new Error('Quick View payload missing');
      const product = JSON.parse(payloadScript.textContent);

      const variantsHtml = doc.querySelector('[data-vc-qv-variants-html]')?.innerHTML ?? '';
      const paymentTermsHtml = doc.querySelector('[data-vc-qv-payment-terms-html]')?.innerHTML ?? '';

      this.#product = product;
      this.#populate(product, variantsHtml, paymentTermsHtml);
    } catch (error) {
      console.error('[vc-quick-view]', error);
      this.close();
    } finally {
      this.#setLoading(false);
    }
  }

  close() {
    this.#dialog?.close();
  }

  #setLoading(isLoading) {
    this.querySelector('[data-vc-qv-loading]')?.toggleAttribute('data-active', isLoading);
    this.querySelector('[data-vc-qv-body]')?.toggleAttribute('hidden', isLoading);
  }

  #reset() {
    this.#product = null;
    this.#selectedVariant = null;
    this.#selectedOptions = {};
    this.#images = [];
    this.#activeImageIndex = 0;
    const qtyInput = /** @type {HTMLInputElement | null} */ (this.querySelector('[data-vc-qv-qty-input]'));
    if (qtyInput) qtyInput.value = '1';
    const variantsContainer = this.querySelector('[data-vc-qv-variants]');
    if (variantsContainer) variantsContainer.innerHTML = '';
    const paymentTermsContainer = this.querySelector('[data-vc-qv-payment-terms]');
    if (paymentTermsContainer) paymentTermsContainer.innerHTML = '';
  }

  /**
   * @param {object} product
   * @param {string} variantsHtml
   * @param {string} paymentTermsHtml
   */
  #populate(product, variantsHtml, paymentTermsHtml) {
    this.#images = product.images.map((src) => ({ src, alt: product.title }));
    this.#activeImageIndex = 0;

    const titleLink = /** @type {HTMLAnchorElement | null} */ (this.querySelector('[data-vc-qv-title-link]'));
    if (titleLink) {
      titleLink.textContent = product.title;
      titleLink.href = product.url;
    }

    const viewDetails = /** @type {HTMLAnchorElement | null} */ (this.querySelector('[data-vc-qv-view-details]'));
    if (viewDetails) viewDetails.href = product.url;

    const descriptionEl = this.querySelector('[data-vc-qv-description]');
    if (descriptionEl) descriptionEl.textContent = product.description;

    const variantsContainer = this.querySelector('[data-vc-qv-variants]');
    if (variantsContainer) variantsContainer.innerHTML = variantsHtml;

    const paymentTermsContainer = this.querySelector('[data-vc-qv-payment-terms]');
    if (paymentTermsContainer) paymentTermsContainer.innerHTML = paymentTermsHtml;

    // Seed selection from the server's own is-selected marking (native
    // selected_or_first_available_variant logic), keyed by 1-based option position.
    this.#selectedOptions = {};
    variantsContainer?.querySelectorAll('.vc-quick-view__swatch-btn.is-selected').forEach((button) => {
      const btn = /** @type {HTMLButtonElement} */ (button);
      const position = btn.dataset.optionPosition;
      if (position) this.#selectedOptions[position] = btn.dataset.optionValue ?? '';
    });

    this.#renderThumbnails();
    this.#showImage(0);
    this.#updateSelectedVariant();
  }

  /**
   * Shopify's <shopify-payment-terms> "Learn more" trigger opens a "Get it now, pay later" popup
   * by appending a new shadow-hosted element directly to <body> (a portal, escaping any
   * ancestor's overflow/stacking context) -- but that puts it in the normal document stacking
   * order, and an open showModal() dialog like ours always paints above any such content
   * regardless of z-index, since native modal dialogs live in the browser's separate "top layer".
   * The popup's own styling lives in that shadow root's adoptedStyleSheets, which stays attached
   * to its host element wherever the host moves in the DOM -- so moving the host itself (not its
   * descendants, which would strip the styling) into our own dialog carries the correct styling
   * along with it and lets it finally render above our modal instead of invisibly behind it.
   * Runs once for the element's whole lifetime, not per #populate() call, since the trigger can
   * fire at any point while a product is loaded.
   */
  #watchForInstallmentsPopup() {
    const observer = new MutationObserver(() => {
      if (!this.#dialog?.open) return;
      for (const child of document.body.children) {
        if (
          !this.#dialog.contains(child) &&
          child.shadowRoot?.querySelector('section[role="dialog"]')
        ) {
          this.#dialog.appendChild(child);
        }
      }
    });
    observer.observe(document.body, { childList: true });
  }

  /** @param {HTMLButtonElement} button */
  #selectSwatch(button) {
    const position = button.dataset.optionPosition;
    const value = button.dataset.optionValue;
    if (!position || value === undefined) return;
    this.#selectedOptions[position] = value;

    const group = button.closest('.vc-quick-view__swatches');
    group?.querySelectorAll('.vc-quick-view__swatch-btn').forEach((el) => {
      const isSelected = el === button;
      el.classList.toggle('is-selected', isSelected);
      el.setAttribute('aria-pressed', String(isSelected));
    });

    const label = button.closest('.vc-quick-view__option')?.querySelector('[data-vc-qv-option-value-label]');
    if (label) label.textContent = value;

    this.#updateSelectedVariant();
  }

  #updateSelectedVariant() {
    const product = this.#product;
    if (!product) return;

    const entries = Object.entries(this.#selectedOptions);
    const variant =
      product.variants.find(
        (/** @type {any} */ v) => entries.every(([position, value]) => v.options[Number(position) - 1] === value)
      ) || product.variants[0];
    this.#selectedVariant = variant || null;

    const priceEl = this.querySelector('[data-vc-qv-price]');
    if (priceEl && variant) {
      priceEl.innerHTML = '';
      const priceSpan = document.createElement('span');
      priceSpan.className = 'vc-quick-view__price-current';
      priceSpan.textContent = variant.price_formatted;
      priceEl.appendChild(priceSpan);
      if (variant.compare_at_price > variant.price) {
        const compareSpan = document.createElement('span');
        compareSpan.className = 'vc-quick-view__price-compare';
        compareSpan.textContent = variant.compare_at_price_formatted;
        priceEl.appendChild(compareSpan);
      }
    }

    const savingsEl = this.querySelector('[data-vc-qv-savings]');
    if (savingsEl) {
      if (variant && variant.compare_at_price > variant.price && variant.savings_percent > 0) {
        savingsEl.textContent = `You save: ${variant.savings_percent}% (${variant.savings_amount_formatted})`;
        savingsEl.hidden = false;
      } else {
        savingsEl.textContent = '';
        savingsEl.hidden = true;
      }
    }

    const addButton = /** @type {HTMLButtonElement | null} */ (this.querySelector('[data-vc-qv-add-to-cart]'));
    const addText = this.querySelector('[data-vc-qv-add-to-cart-text]');
    if (addButton) addButton.disabled = !variant || !variant.available;
    if (addText) addText.textContent = variant && !variant.available ? 'Sold Out' : 'Add To Cart';

    if (variant?.featured_image) {
      const index = this.#images.findIndex((image) => image.src === variant.featured_image);
      if (index !== -1) this.#showImage(index);
    }
  }

  #renderThumbnails() {
    const container = this.querySelector('[data-vc-qv-thumbs]');
    if (!container) return;
    container.innerHTML = '';

    const total = this.#images.length;
    const thumbArrows = this.querySelectorAll('[data-vc-qv-thumb-prev], [data-vc-qv-thumb-next]');
    thumbArrows.forEach((arrow) => arrow.toggleAttribute('hidden', total <= 1));
    if (total <= 1) return;

    this.#images.forEach((image, index) => {
      const thumb = document.createElement('button');
      thumb.type = 'button';
      thumb.className = 'vc-quick-view__thumb';
      thumb.setAttribute('aria-label', `Show image ${index + 1}`);
      const img = document.createElement('img');
      img.src = this.#resizeImage(image.src, 160);
      img.alt = '';
      thumb.appendChild(img);
      thumb.addEventListener('click', () => this.#showImage(index));
      container.appendChild(thumb);
    });
  }

  /** Scrolls the thumbnail strip by one viewport-width "page", matching Flickity's per-click behavior. */
  /** @param {number} direction -1 for previous, 1 for next */
  #scrollThumbs(direction) {
    const container = /** @type {HTMLElement | null} */ (this.querySelector('[data-vc-qv-thumbs]'));
    if (!container) return;
    container.scrollBy({ left: direction * container.clientWidth, behavior: 'smooth' });
  }

  /** @param {number} index */
  #showImage(index) {
    if (this.#images.length === 0) return;
    const total = this.#images.length;
    this.#activeImageIndex = ((index % total) + total) % total;
    const image = this.#images[this.#activeImageIndex];

    const mainImage = /** @type {HTMLImageElement | null} */ (this.querySelector('[data-vc-qv-main-image]'));
    if (mainImage) {
      mainImage.src = this.#resizeImage(image.src, 900);
      mainImage.alt = image.alt || '';
    }

    const arrows = this.querySelectorAll('[data-vc-qv-prev], [data-vc-qv-next]');
    arrows.forEach((arrow) => arrow.toggleAttribute('hidden', total <= 1));

    const thumbs = /** @type {NodeListOf<HTMLElement>} */ (this.querySelectorAll('[data-vc-qv-thumbs] .vc-quick-view__thumb'));
    thumbs.forEach((thumb, thumbIndex) => {
      const isActive = thumbIndex === this.#activeImageIndex;
      thumb.classList.toggle('is-active', isActive);
      if (isActive) thumb.scrollIntoView({ behavior: 'smooth', inline: 'nearest', block: 'nearest' });
    });
  }

  /**
   * @param {string} src
   * @param {number} width
   */
  #resizeImage(src, width) {
    if (!src) return src;
    return src.includes('?') ? `${src}&width=${width}` : `${src}?width=${width}`;
  }

  /** @param {number} delta */
  #adjustQuantity(delta) {
    const input = /** @type {HTMLInputElement | null} */ (this.querySelector('[data-vc-qv-qty-input]'));
    if (!input) return;
    const next = Math.max(1, (parseInt(input.value, 10) || 1) + delta);
    input.value = String(next);
  }

  async #addToCart() {
    const variant = this.#selectedVariant;
    if (!variant || !variant.available) return;

    const addButton = /** @type {HTMLButtonElement | null} */ (this.querySelector('[data-vc-qv-add-to-cart]'));
    const addText = this.querySelector('[data-vc-qv-add-to-cart-text]');
    const quantityInput = /** @type {HTMLInputElement | null} */ (this.querySelector('[data-vc-qv-qty-input]'));
    const quantity = Math.max(1, parseInt(quantityInput?.value ?? '1', 10) || 1);

    if (addButton) addButton.disabled = true;
    if (addText) addText.textContent = 'Adding...';

    let deferredResolve;
    let deferredReject;
    const deferredPromise = new Promise((resolve, reject) => {
      deferredResolve = resolve;
      deferredReject = reject;
    });

    // Dispatched from the <dialog> itself (not this wrapper element): cart-drawer.js checks
    // `event.target.closest('dialog:modal')` to defer its auto-open until an open modal dialog
    // closes first (avoiding two overlapping dialogs). `.closest()` only matches the element
    // itself or its ancestors, so the dialog has to be the actual dispatch target.
    (this.#dialog ?? this).dispatchEvent(
      new CartLinesUpdateEvent({
        action: 'add',
        context: 'vc-quick-view',
        lines: [{ merchandiseId: `gid://shopify/ProductVariant/${variant.id}`, quantity }],
        promise: deferredPromise,
      })
    );

    try {
      const response = await fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ id: variant.id, quantity }),
      });
      const result = await response.json();

      if (!response.ok) {
        throw new Error(result.description || result.message || 'Add to cart failed');
      }

      const cartResponse = await fetch('/cart.js');
      const ajaxCart = await cartResponse.json();
      deferredResolve({
        cart: CartLinesUpdateEvent.createCartFromAjaxResponse(ajaxCart),
        detail: { didError: false, itemCount: ajaxCart.item_count, source: 'vc-quick-view' },
      });

      this.close();
    } catch (error) {
      console.error('[vc-quick-view]', error);
      deferredReject(error);
      if (addText) addText.textContent = 'Couldn’t add - try again';
      if (addButton) addButton.disabled = false;
    }
  }
}

if (!customElements.get('vc-quick-view')) {
  customElements.define('vc-quick-view', VcQuickView);
}
